import { newUid } from '@/util';
import { Button, IconButton } from '@teo/components';
import { CrossFilled, Plus } from '@teo/components/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const createEmptyGroup = () => ({
  uid: newUid(10),
  title: '',
  items: [],
});

const createEmptyItem = () => ({
  uid: newUid(10),
  label: '',
  isNew: true,
});

export default function SortGroupsEditContent({ value, onChange }) {
  const { t } = useTranslation();
  const [internalGroups, setInternalGroups] = useState(value);

  const groups = value || internalGroups;

  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);

  const itemInputRefs = useRef({});

  const setGroups = (next) => {
    if (!value) {
      setInternalGroups(next);
    }
    if (onChange) {
      onChange(next);
    }
  };

  const updateGroupTitle = (groupId, title) => {
    setGroups(
      groups?.map((group) =>
        group.uid === groupId ? { ...group, title } : group
      )
    );
  };

  const addGroup = () => {
    const newGroup = createEmptyGroup();
    setGroups([...groups, newGroup]);
    setEditingGroupId(newGroup.uid);
  };

  const removeGroup = (groupId) => {
    setGroups(groups?.filter((group) => group.uid !== groupId));

    if (editingGroupId === groupId) {
      setEditingGroupId(null);
    }

    if (editingItem?.groupId === groupId) {
      setEditingItem(null);
    }
  };

  const addItem = (groupId) => {
    const newItem = createEmptyItem();

    const next = groups?.map((group) =>
      group.uid === groupId
        ? {
            ...group,
            items: [...group.items, newItem],
          }
        : group
    );

    setGroups(next);
    setEditingItem({ groupId, itemId: newItem.uid });

    setTimeout(() => {
      const refKey = `${groupId}:${newItem.uid}`;
      itemInputRefs.current[refKey]?.focus();
    }, 0);
  };

  const removeItem = (groupId, itemId) => {
    const next = groups?.map((group) =>
      group.uid === groupId
        ? {
            ...group,
            items: group.items.filter((item) => item.uid !== itemId),
          }
        : group
    );

    setGroups(next);

    if (
      editingItem &&
      editingItem.groupId === groupId &&
      editingItem.itemId === itemId
    ) {
      setEditingItem(null);
    }
  };

  const updateItemLabel = (groupId, itemId, label) => {
    const next = groups?.map((group) =>
      group.uid === groupId
        ? {
            ...group,
            items: group.items.map((item) =>
              item.uid === itemId ? { ...item, label } : item
            ),
          }
        : group
    );

    setGroups(next);
  };

  const confirmItemEdit = (groupId, itemId) => {
    const group = groups?.find((g) => g.uid === groupId);
    const item = group?.items.find((x) => x.uid === itemId);

    if (!item) return;

    const trimmed = (item.label || '').trim();

    if (!trimmed) {
      removeItem(groupId, itemId);
      return;
    }

    const next = groups?.map((g) =>
      g.uid === groupId
        ? {
            ...g,
            items: g.items.map((x) =>
              x.uid === itemId ? { ...x, label: trimmed, isNew: false } : x
            ),
          }
        : g
    );

    setGroups(next);
    setEditingItem(null);
  };

  const startEditingItem = (groupId, itemId) => {
    setEditingItem({ groupId, itemId });

    setTimeout(() => {
      const refKey = `${groupId}:${itemId}`;
      itemInputRefs.current[refKey]?.focus();
    }, 0);
  };

  const moveItemToGroup = (itemId, fromGroupId, toGroupId) => {
    if (!itemId || !fromGroupId || !toGroupId) return;
    if (fromGroupId === toGroupId) return;

    const sourceGroup = groups?.find((g) => g.uid === fromGroupId);
    const movedItem = sourceGroup?.items.find((item) => item.uid === itemId);

    if (!movedItem) return;

    const next = groups?.map((group) => {
      if (group.uid === fromGroupId) {
        return {
          ...group,
          items: group.items.filter((item) => item.uid !== itemId),
        };
      }

      if (group.uid === toGroupId) {
        return {
          ...group,
          items: [...group.items, movedItem],
        };
      }

      return group;
    });

    setGroups(next);

    if (
      editingItem &&
      editingItem.groupId === fromGroupId &&
      editingItem.itemId === itemId
    ) {
      setEditingItem({
        groupId: toGroupId,
        itemId,
      });
    }
  };

  const handleMouseUpGlobal = () => {
    if (draggingItem && dragOverGroupId) {
      moveItemToGroup(
        draggingItem.itemId,
        draggingItem.fromGroupId,
        dragOverGroupId
      );
    }

    setDraggingItem(null);
    setDragOverGroupId(null);
    setDragPreview(null);
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUpGlobal);

    return () => {
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!draggingItem) return;

      setDragPreview({
        x: e.clientX,
        y: e.clientY,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [draggingItem]);

  const draggedGroup = draggingItem
    ? groups?.find((g) => g.uid === draggingItem.fromGroupId)
    : null;

  const draggedItem = draggingItem
    ? draggedGroup?.items.find((i) => i.uid === draggingItem.itemId)
    : null;

  return (
    // <div className="relative flex flex-wrap gap-5 p-4">
    <div className="relative  grid grid-cols-3 gap-3">
      {groups?.map((group) => {
        const isDragOver = dragOverGroupId === group.uid;
        const isSourceGroup = draggingItem?.fromGroupId === group.uid;

        return (
          <div
            key={group.uid}
            onMouseEnter={() => {
              if (draggingItem) {
                setDragOverGroupId(group.uid);
              }
            }}
            className={`rounded-lg border border-grey-02 bg-white p-4 transition ${
              isDragOver
                ? 'border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.15)]'
                : 'border-[#d9d9d9]'
            }`}
          >
            <div className="mb-3 flex w-full flex-row items-center gap-1">
              <div className="w-full">
                <input
                  value={group.title}
                  onChange={(e) => updateGroupTitle(group.uid, e.target.value)}
                  onFocus={() => setEditingGroupId(group.uid)}
                  onBlur={() => setEditingGroupId(null)}
                  className="w-full rounded-md border-none bg-transparent text-sm font-medium text-grey-08 placeholder-gray-400 outline-none"
                  placeholder={t('widgets.edit.sort_groups.group_title')}
                />
              </div>
              <IconButton
                className="ml-auto !text-grey-06"
                Icon={CrossFilled}
                variant="ghost"
                onClick={() => removeGroup(group.uid)}
              />
            </div>

            {group?.items?.length > 0 && (
              <div className="mb-2 flex flex-col gap-2">
                {draggingItem && isDragOver && !isSourceGroup && (
                  <div className="flex h-[36px] items-center rounded-[4px] border border-dashed border-blue-400 bg-blue-50 px-4 text-sm font-medium text-blue-500">
                    {t('widgets.edit.sort_groups.drop_here')}
                  </div>
                )}

                {group?.items?.map((item) => {
                  const isEditing =
                    editingItem &&
                    editingItem.groupId === group.uid &&
                    editingItem.itemId === item.uid;

                  const isDraggingCurrentItem =
                    draggingItem &&
                    draggingItem.itemId === item.uid &&
                    draggingItem.fromGroupId === group.uid;

                  const refKey = `${group.uid}:${item.uid}`;

                  return (
                    <div
                      key={item.uid}
                      className={`flex items-center gap-2 rounded-[4px] p-2 shadow-[0_1px_4px_0_rgba(0,0,0,0.2)] transition  ${
                        isDraggingCurrentItem
                          ? 'border-blue-300 bg-blue-50 opacity-60'
                          : 'border-[#e3e3e3] bg-grey-01'
                      } 
                    ${
                      isEditing
                        ? 'border border-secondary-04 bg-white !p-1'
                        : ''
                    }`}
                    >
                      {!isEditing && (
                        <button
                          type="button"
                          onMouseDown={() =>
                            setDraggingItem({
                              itemId: item.uid,
                              fromGroupId: group.uid,
                            })
                          }
                          className="flex w-5 !cursor-grab select-none py-[2px] text-[#a1a1a1] active:!cursor-grabbing"
                          title="Drag item"
                        >
                          <img
                            src="/images/icons/dragHandle.svg"
                            alt="dragHandle button"
                            className="m-auto w-3 !cursor-grab active:!cursor-grabbing"
                          />
                        </button>
                      )}

                      <div className="flex-1">
                        {isEditing ? (
                          <div className="overflow-hidden rounded-[6px] bg-grey-01">
                            <input
                              ref={(el) => {
                                itemInputRefs.current[refKey] = el;
                              }}
                              type="text"
                              value={item.label || ''}
                              onChange={(e) =>
                                updateItemLabel(
                                  group.uid,
                                  item.uid,
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  confirmItemEdit(group.uid, item.uid);
                                }

                                if (e.key === 'Escape') {
                                  if (
                                    item.isNew &&
                                    !(item.label || '').trim()
                                  ) {
                                    removeItem(group.uid, item.uid);
                                  } else {
                                    setEditingItem(null);
                                  }
                                }
                              }}
                              onBlur={() => {
                                confirmItemEdit(group.uid, item.uid);
                              }}
                              placeholder="Enter label"
                              className="w-full min-w-0 border-0 bg-grey-01 px-3 py-[3px] text-sm text-grey-08 outline-none focus:border-transparent focus:outline-none focus:ring-0"
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              startEditingItem(group.uid, item.uid)
                            }
                            className="block w-full cursor-text text-left text-sm text-grey-08"
                          >
                            {item.label}
                          </button>
                        )}
                      </div>

                      {!isEditing && (
                        <IconButton
                          size="sm"
                          className="ml-auto !p-0 !text-grey-04"
                          Icon={CrossFilled}
                          variant="ghost"
                          onClick={() => removeItem(group.uid, item.uid)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <Button
              onClick={() => addItem(group.uid)}
              variant="outline"
              className=" w-full"
            >
              {t('widgets.edit.sort_groups.add_item')}
            </Button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addGroup}
        className="flex min-h-[166px] flex-col items-center justify-center rounded-lg border border-dashed border-grey-02 bg-white hover:bg-[#fafafa]"
      >
        <Plus className="w-5 text-grey-04" />
        <span className="text-sm text-grey-06">
          {t('widgets.edit.sort_groups.add_group')}
        </span>
      </button>

      {draggingItem && dragPreview && draggedItem && (
        <div
          style={{
            position: 'fixed',
            top: dragPreview.y + 12,
            left: dragPreview.x + 12,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          className="min-w-[180px] rounded-[4px] border border-[#d9d9d9] bg-white px-3 py-1 text-[16px] font-medium text-[#3a3a3a] opacity-95 shadow-lg"
        >
          {draggedItem.label || 'New item'}
        </div>
      )}
    </div>
  );
}
